import { authStore } from '@/services/auth/store';
import { activeAccountStore } from '@customerio/hydra-nav-state';
import { omitBy } from 'lodash-es';
import { Config } from '@/utils/config';

export const Environment = {
  DEVELOPMENT: 'development',
  STANDALONE: 'standalone',
  PRODUCTION: 'production',
} as const;
export type Environment = (typeof Environment)[keyof typeof Environment];

export const Region = {
  US: 'us',
  EU: 'eu',
} as const;
export type Region = (typeof Region)[keyof typeof Region];

export const apiEndpoints = (): Record<Environment, Partial<Record<Region, string>>> => {
  return {
    [Environment.DEVELOPMENT]: {
      [Region.US]: window.origin,
      [Region.EU]: window.origin,
    },
    [Environment.STANDALONE]: {
      [Region.US]: 'https://us.fly-standalone.customer.io',
      [Region.EU]: 'https://us.fly-standalone.customer.io',
    },
    [Environment.PRODUCTION]: {
      [Region.US]: 'https://us.fly.customer.io',
      [Region.EU]: 'https://eu.fly.customer.io',
    },
  };
};

export const EDGE_ENDPOINTS: Record<Environment, Partial<Record<Region, string>>> = {
  [Environment.DEVELOPMENT]: {
    [Region.US]: 'http://localhost:3000',
    [Region.EU]: 'http://localhost:3000',
  },
  [Environment.STANDALONE]: {
    [Region.US]: 'https://staging-c-cdp-edge.cio.host',
    [Region.EU]: 'https://staging-c-cdp-edge.cio.host',
  },
  [Environment.PRODUCTION]: {
    [Region.US]: 'https://cdp.customer.io',
    [Region.EU]: 'https://cdp-eu.customer.io',
  },
};

export const getCurrentEnvironment = () => {
  if (import.meta.env.DEV || __CIO_ENV__ === 'devbox') {
    return Environment.DEVELOPMENT;
  }

  // In production, we don't want to match against the development endpoints.
  const nonDevEndpoints = omitBy(apiEndpoints(), (value, key) => key === Environment.DEVELOPMENT);

  // Find environment based on the origin URL.
  for (const [environment, regions] of Object.entries(nonDevEndpoints)) {
    for (const [, url] of Object.entries(regions)) {
      // Do a partial match.
      // If the user is on https://fly.customer.io, it should match https://us.fly.customer.io
      // and/or https://eu.fly.customer.io.
      // If they're on https://fly-standalone.customer.io, it should match
      // https://us.fly-standalone.customer.io and/or https://eu.fly-standalone.customer.io.
      if (url.includes(window.location.host)) {
        return environment as Environment;
      }
    }
  }

  // Otherwise, assume production.
  return Environment.PRODUCTION;
};

export const getCurrentRegion = () => {
  const regionFromHydra = activeAccountStore.get()?.regionName as Region;

  return regionFromHydra ?? authStore.get()?.account?.region ?? Region.US;
};

function getApiUrlForRegion(environment: Environment, region: Region) {
  const host = apiEndpoints()[environment][region] ?? '';
  return `${host}/cdp/api`;
}

export function getCurrentApiUrl() {
  return getApiUrlForRegion(getCurrentEnvironment(), getCurrentRegion());
}

export function getEdgeUrlForRegion(environment: Environment, region: Region) {
  return Config.edgeUrl ?? EDGE_ENDPOINTS[environment][region];
}

export function getCurrentEdgeUrl() {
  return getEdgeUrlForRegion(getCurrentEnvironment(), getCurrentRegion());
}
